import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Collapse,
  Empty,
  Input,
  Modal,
  Progress,
  Radio,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import {
  ArrowLeftOutlined,
  BookOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  CopyOutlined,
  FileTextOutlined,
  FolderOpenOutlined,
  LinkOutlined,
  LoadingOutlined,
  MinusCircleOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  SearchOutlined,
  SettingOutlined,
  ThunderboltOutlined,
  TeamOutlined,
  UploadOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import * as api from '../api/client';

const { Text, Title, Paragraph } = Typography;
const { TextArea } = Input;

interface Props {
  kbId: string;
  userId?: string;
  onRefreshList?: () => void;
  onBack?: () => void;
  initialTab?: 'overview' | 'documents' | 'import' | 'search' | 'suggested' | 'chunks' | 'sharing' | 'settings';
}

interface KBDoc {
  id: string;
  title: string;
  source_type: string;
  status: string;
  content_type?: string;
  chunk_count?: number;
  error_message?: string;
  metadata_json?: string;
  processing_progress_percent?: number;
  processing_stage?: string;
  created_at?: string;
}

interface KBChunk {
  id: string;
  document_id: string;
  chunk_index: number;
  text: string;
  token_count: number;
  created_at?: string;
  metadata?: Record<string, any>;
}

interface SearchResult {
  chunk_id: string;
  document_id: string;
  document_title: string;
  text: string;
  score: number;
  metadata: Record<string, any>;
}

interface ArtifactOption {
  id: string;
  filename: string;
  task_id?: string;
  content_type?: string;
  size_bytes?: number;
  created_at?: string;
}

interface SmartSourceItem {
  name: string;
  url: string;
  status: 'pending' | 'processing' | 'done' | 'failed' | 'cancelled';
  chunkCount?: number;
  error?: string;
}

interface SuggestedTaskTemplate {
  id: string;
  knowledge_base_id: string;
  name: string;
  description: string;
  suggested_prompt: string;
  recommended_mode: 'chat' | 'deep_search' | 'autonomous';
  suggested_knowledge_base_ids: string[];
  tags: string[];
  created_at?: string;
  updated_at?: string;
}

const KB_UPLOAD_ACCEPT = '.pdf,.docx,.txt,.md,.csv,.xlsx,.html,.htm,.json,.jsonl,.sav,.yaml,.yml,.png,.jpg,.jpeg,.gif,.webp,.bmp,.tif,.tiff';
const KB_UPLOAD_EXTENSIONS = new Set(
  KB_UPLOAD_ACCEPT.split(',').map(ext => ext.toLowerCase()),
);

const DOC_STATUS_COLOR: Record<string, string> = {
  INDEXED: 'green',
  PROCESSING: 'blue',
  FAILED: 'red',
  PENDING: 'orange',
};

const SOURCE_COLOR: Record<string, string> = {
  TEXT: 'purple',
  UPLOAD: 'geekblue',
  URL: 'cyan',
  ARTIFACT: 'gold',
};

const KnowledgeBaseDetail: React.FC<Props> = ({ kbId, userId = '', onRefreshList, onBack, initialTab = 'overview' }) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [kb, setKb] = useState<any>(null);
  const [docs, setDocs] = useState<KBDoc[]>([]);
  const [chunks, setChunks] = useState<KBChunk[]>([]);
  const [loadingKb, setLoadingKb] = useState(false);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [loadingChunks, setLoadingChunks] = useState(false);

  const [importTitle, setImportTitle] = useState('');
  const [importText, setImportText] = useState('');
  const [importUrl, setImportUrl] = useState('');
  const [importUrlTitle, setImportUrlTitle] = useState('');
  const [artifactId, setArtifactId] = useState('');
  const [artifactTitle, setArtifactTitle] = useState('');
  const [importing, setImporting] = useState(false);
  const [artifacts, setArtifacts] = useState<ArtifactOption[]>([]);
  const [loadingArtifacts, setLoadingArtifacts] = useState(false);
  const [artifactSearch, setArtifactSearch] = useState('');

  const [searchQuery, setSearchQuery] = useState('');
  const [searchMode, setSearchMode] = useState<'keyword' | 'semantic'>('keyword');
  const [searchLimit, setSearchLimit] = useState<number>(8);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [semanticFallback, setSemanticFallback] = useState(false);

  const [chunkDocFilter, setChunkDocFilter] = useState<string>('');
  const [chunkQuery, setChunkQuery] = useState('');

  const [settingsName, setSettingsName] = useState('');
  const [settingsDescription, setSettingsDescription] = useState('');
  const [settingsChunking, setSettingsChunking] = useState('recursive');
  const [settingsProvider, setSettingsProvider] = useState('openai');
  const [settingsModel, setSettingsModel] = useState('text-embedding-3-large');
  const [settingsStatus, setSettingsStatus] = useState('ACTIVE');
  const [savingSettings, setSavingSettings] = useState(false);

  const [smartSourceOpen, setSmartSourceOpen] = useState(false);
  const [smartSourceInput, setSmartSourceInput] = useState('');
  const [smartSourceItems, setSmartSourceItems] = useState<SmartSourceItem[]>([]);
  const [smartSourceRunning, setSmartSourceRunning] = useState(false);
  const [smartSourceDone, setSmartSourceDone] = useState(false);
  const cancelSmartSourceRef = useRef(false);
  const pendingUploadDocIdsRef = useRef<Set<string>>(new Set());
  const notifiedUploadDocIdsRef = useRef<Set<string>>(new Set());
  const folderUploadInputRef = useRef<HTMLInputElement | null>(null);
  const [suggestedTasks, setSuggestedTasks] = useState<SuggestedTaskTemplate[]>([]);
  const [loadingSuggestedTasks, setLoadingSuggestedTasks] = useState(false);
  const [generatingSuggestedTasks, setGeneratingSuggestedTasks] = useState(false);
  const [savingSuggestedTaskId, setSavingSuggestedTaskId] = useState<string>('');
  const [launchingSuggestedTaskId, setLaunchingSuggestedTaskId] = useState<string>('');
  const [shares, setShares] = useState<api.KnowledgeBaseShare[]>([]);
  const [loadingShares, setLoadingShares] = useState(false);
  const [shareUserId, setShareUserId] = useState('');
  const [sharingKb, setSharingKb] = useState(false);

  const isOwner = useMemo(
    () => !!kb?.user_id && !!userId && String(kb.user_id).toLowerCase() === userId.toLowerCase(),
    [kb?.user_id, userId],
  );

  useEffect(() => setActiveTab(initialTab), [initialTab, kbId]);

  const loadArtifacts = useCallback(async () => {
    setLoadingArtifacts(true);
    try {
      const res = await api.listArtifacts({ limit: 200 });
      // Support both { artifacts: [...] } and plain array responses from the artifact service
      const list = res.data.artifacts ?? (Array.isArray(res.data) ? res.data : []);
      setArtifacts(list);
    } catch {
      // Non-fatal: artifact service may not be available
    } finally {
      setLoadingArtifacts(false);
    }
  }, []);

  const loadKb = useCallback(async () => {
    setLoadingKb(true);
    try {
      const res = await api.getKnowledgeBase(kbId);
      setKb(res.data);
      setSettingsName(res.data.name || '');
      setSettingsDescription(res.data.description || '');
      setSettingsChunking(res.data.chunking_strategy || 'recursive');
      setSettingsProvider(res.data.embedding_provider || 'openai');
      setSettingsModel(res.data.embedding_model || 'text-embedding-3-large');
      setSettingsStatus(res.data.status || 'ACTIVE');
    } catch {
      message.error('Failed to load KB details');
    } finally {
      setLoadingKb(false);
    }
  }, [kbId]);

  const loadDocs = useCallback(async () => {
    setLoadingDocs(true);
    try {
      const res = await api.listKnowledgeDocuments(kbId);
      const nextDocs = (res.data.documents || []).map((doc: KBDoc) => {
        let metadata: Record<string, any> = {};
        try {
          metadata = JSON.parse(doc.metadata_json || '{}');
        } catch {
          metadata = {};
        }
        const percent = Number(metadata.processing_progress_percent);
        return {
          ...doc,
          processing_progress_percent: Number.isFinite(percent) ? Math.max(0, Math.min(100, percent)) : undefined,
          processing_stage: typeof metadata.processing_stage === 'string' ? metadata.processing_stage : undefined,
        };
      });
      setDocs(nextDocs);
    } catch {
      message.error('Failed to load documents');
    } finally {
      setLoadingDocs(false);
    }
  }, [kbId]);

  const loadChunks = useCallback(async () => {
    setLoadingChunks(true);
    try {
      const res = await api.listKnowledgeChunks(kbId, {
        document_id: chunkDocFilter || undefined,
      });
      setChunks(res.data.chunks || []);
    } catch {
      message.error('Failed to load chunks');
    } finally {
      setLoadingChunks(false);
    }
  }, [kbId, chunkDocFilter]);

  const loadSuggestedTasks = useCallback(async () => {
    setLoadingSuggestedTasks(true);
    try {
      const res = await api.listKnowledgeBaseSuggestedTasks(kbId, { limit: 100 });
      setSuggestedTasks(res.data.suggested_tasks || []);
    } catch {
      message.error('Failed to load suggested tasks');
    } finally {
      setLoadingSuggestedTasks(false);
    }
  }, [kbId]);

  const loadShares = useCallback(async () => {
    setLoadingShares(true);
    try {
      const res = await api.listKnowledgeBaseShares(kbId);
      setShares(res.data.shares || []);
    } catch {
      message.error('Failed to load sharing settings');
    } finally {
      setLoadingShares(false);
    }
  }, [kbId]);

  useEffect(() => {
    loadKb();
    loadDocs();
    loadChunks();
    loadSuggestedTasks();
  }, [kbId, loadKb, loadDocs, loadChunks, loadSuggestedTasks]);

  useEffect(() => {
    if (kb && isOwner) loadShares();
    if (kb && !isOwner) setShares([]);
  }, [kb, isOwner, loadShares]);

  useEffect(() => {
    if ((activeTab === 'sharing' || activeTab === 'settings') && kb && !isOwner) {
      setActiveTab('overview');
    }
  }, [activeTab, kb, isOwner]);

  // Load artifacts when the import tab becomes active
  useEffect(() => {
    if (activeTab === 'import') loadArtifacts();
  }, [activeTab, loadArtifacts]);

  useEffect(() => {
    if (pendingUploadDocIdsRef.current.size === 0) return undefined;
    const timer = window.setInterval(() => {
      loadDocs();
      loadKb();
    }, 4000);
    return () => window.clearInterval(timer);
  }, [docs, loadDocs, loadKb]);

  useEffect(() => {
    if (pendingUploadDocIdsRef.current.size === 0) return;
    const pendingIds = Array.from(pendingUploadDocIdsRef.current);
    pendingIds.forEach((docId) => {
      const doc = docs.find((row) => row.id === docId);
      if (!doc) return;
      if (doc.status === 'PROCESSING' || notifiedUploadDocIdsRef.current.has(docId)) return;
      pendingUploadDocIdsRef.current.delete(docId);
      notifiedUploadDocIdsRef.current.add(docId);
      if (doc.status === 'INDEXED') {
        Modal.success({
          className: 'praxys-dark-confirm-modal',
          title: 'Knowledge Base import complete',
          content: (
            <div>
              <Text>
                "{doc.title}" finished processing in {kb?.name || 'this Knowledge Base'}.
              </Text>
              <br />
              <Button type="link" style={{ paddingLeft: 0 }} onClick={() => setActiveTab('documents')}>
                Open Knowledge Base documents
              </Button>
            </div>
          ),
        });
      } else if (doc.status === 'FAILED') {
        Modal.error({
          className: 'praxys-dark-confirm-modal',
          title: 'Knowledge Base import failed',
          content: doc.error_message || `"${doc.title}" failed during processing.`,
        });
      }
    });
  }, [docs, kb?.name]);

  useEffect(() => {
    if (!folderUploadInputRef.current) return;
    folderUploadInputRef.current.setAttribute('webkitdirectory', '');
    folderUploadInputRef.current.setAttribute('directory', '');
  }, []);

  const docTitleById = useMemo(() => {
    const map: Record<string, string> = {};
    docs.forEach((d) => { map[d.id] = d.title; });
    return map;
  }, [docs]);

  const normalizeSearchResults = useCallback((raw: any): SearchResult[] => {
    const rows = raw?.results || raw?.chunks || (Array.isArray(raw) ? raw : []);
    if (!Array.isArray(rows)) return [];
    return rows.map((row: any) => ({
      chunk_id: row.chunk_id || row.id || '',
      document_id: row.document_id || '',
      document_title: row.document_title || docTitleById[row.document_id] || 'Document',
      text: row.text || row.chunk_text || row.content || '',
      score: typeof row.score === 'number' ? row.score : 0,
      metadata: row.metadata || {},
    }));
  }, [docTitleById]);

  const handleImportText = async () => {
    if (!importTitle.trim() || !importText.trim()) {
      message.warning('Title and text are required');
      return;
    }
    setImporting(true);
    try {
      const res = await api.importKnowledgeText(kbId, {
        title: importTitle.trim(),
        text: importText.trim(),
      });
      message.success(`Imported ${res.data.chunk_count || 0} chunks`);
      setImportTitle('');
      setImportText('');
      await Promise.all([loadDocs(), loadKb()]);
      onRefreshList?.();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Text import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleImportUrl = async () => {
    if (!importUrl.trim()) {
      message.warning('URL is required');
      return;
    }
    setImporting(true);
    try {
      const res = await api.importKnowledgeUrl(kbId, {
        url: importUrl.trim(),
        title: importUrlTitle.trim() || undefined,
      });
      message.success(`URL imported: ${res.data.chunk_count || 0} chunks`);
      setImportUrl('');
      setImportUrlTitle('');
      await Promise.all([loadDocs(), loadKb()]);
      onRefreshList?.();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'URL import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleImportArtifact = async () => {
    if (!artifactId.trim()) {
      message.warning('Artifact ID is required');
      return;
    }
    setImporting(true);
    try {
      const res = await api.importKnowledgeArtifact(kbId, {
        artifact_id: artifactId.trim(),
        title: artifactTitle.trim() || undefined,
      });
      message.success(`Artifact imported: ${res.data.chunk_count || 0} chunks`);
      setArtifactId('');
      setArtifactTitle('');
      setArtifactSearch('');
      await Promise.all([loadDocs(), loadKb()]);
      onRefreshList?.();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Artifact import failed');
    } finally {
      setImporting(false);
    }
  };

  const getRelativeUploadPath = (file: File): string => {
    const relativePath = (file as File & { webkitRelativePath?: string }).webkitRelativePath || '';
    return relativePath.trim() || file.name;
  };

  const isSupportedUploadFile = (file: File): boolean => {
    const lowerName = file.name.toLowerCase();
    return Array.from(KB_UPLOAD_EXTENSIONS).some(ext => lowerName.endsWith(ext));
  };

  const uploadFiles = async (files: File[]) => {
    if (!files.length) return;
    setImporting(true);
    try {
      const supportedFiles = files.filter(isSupportedUploadFile);
      const skippedCount = files.length - supportedFiles.length;
      if (!supportedFiles.length) {
        message.warning('No supported files were found in the selected upload.');
        return;
      }

      let totalChunks = 0;
      let queuedCount = 0;
      let completedCount = 0;
      const failedUploads: string[] = [];
      for (const file of supportedFiles) {
        const relativePath = getRelativeUploadPath(file);
        const isDirectoryUpload = relativePath !== file.name;
        try {
          const res = await api.uploadKnowledgeDocument(
            kbId,
            file,
            isDirectoryUpload ? relativePath : undefined,
            isDirectoryUpload ? {
              relative_path: relativePath,
              original_filename: file.name,
            } : undefined,
          );
          completedCount += 1;
          const queued = Boolean(res?.data?.queued);
          const documentId = res?.data?.document?.id as string | undefined;
          if (queued) {
            queuedCount += 1;
            if (documentId) pendingUploadDocIdsRef.current.add(documentId);
          } else {
            totalChunks += Number(res?.data?.chunk_count || 0);
          }
        } catch (err: any) {
          const status = err?.response?.status;
          const detail = err?.response?.data?.detail;
          const errorMsg = status === 413
            ? 'File is too large to upload.'
            : (detail || 'File upload failed');
          failedUploads.push(`${relativePath}: ${errorMsg}`);
        }
      }

      if (completedCount === 0) {
        message.error(failedUploads[0] || 'File upload failed');
        return;
      }

      const parts: string[] = [
        completedCount === 1
          ? (queuedCount === 1
            ? 'Uploaded 1 file for background processing.'
            : `Uploaded 1 file and created ${totalChunks} chunks.`)
          : `Uploaded ${completedCount} files${queuedCount < completedCount ? ` and created ${totalChunks} chunks` : ''}.`,
      ];
      if (queuedCount > 0) {
        parts.push(`${queuedCount} file${queuedCount === 1 ? '' : 's'} queued for background processing.`);
        setActiveTab('documents');
      }
      if (skippedCount > 0) {
        parts.push(`Skipped ${skippedCount} unsupported file${skippedCount === 1 ? '' : 's'}.`);
      }
      if (failedUploads.length > 0) {
        parts.push(`Failed ${failedUploads.length} file${failedUploads.length === 1 ? '' : 's'}.`);
        message.warning(parts.join(' '), 8);
        failedUploads.slice(0, 3).forEach(detail => message.error(detail, 8));
      } else {
        message.success(parts.join(' '), 6);
      }

      await Promise.all([loadDocs(), loadKb()]);
      onRefreshList?.();
    } finally {
      setImporting(false);
    }
  };

  const handleFileUpload = async (file: File) => {
    await uploadFiles([file]);
    return false;
  };

  const handleDirectorySelection = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = '';
    await uploadFiles(selectedFiles);
  };

  const isValidHttpUrl = (url: string): boolean => {
    try {
      const parsed = new URL(url);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const parseSmartSourceInput = (text: string): SmartSourceItem[] => {
    return text
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0)
      .map(line => {
        const tabIdx = line.indexOf('\t');
        if (tabIdx > 0) {
          const name = line.slice(0, tabIdx).trim();
          const url = line.slice(tabIdx + 1).trim();
          if (isValidHttpUrl(url)) return { name, url, status: 'pending' as const };
        }
        // Fallback: detect URL after two or more spaces
        const parts = line.split(/\s{2,}/);
        if (parts.length >= 2) {
          const url = parts[parts.length - 1].trim();
          const name = parts.slice(0, parts.length - 1).join('  ').trim();
          if (isValidHttpUrl(url)) return { name, url, status: 'pending' as const };
        }
        return null;
      })
      .filter((item): item is { name: string; url: string; status: 'pending' } => item !== null)
      .map(item => item as SmartSourceItem);
  };

  const handleSmartSourceStart = async () => {
    const items = parseSmartSourceInput(smartSourceInput);
    if (items.length === 0) {
      message.warning('No valid source/URL pairs found. Use format: Source Name<tab>URL, one per line.');
      return;
    }
    cancelSmartSourceRef.current = false;
    setSmartSourceItems(items);
    setSmartSourceRunning(true);
    setSmartSourceDone(false);

    for (let i = 0; i < items.length; i++) {
      if (cancelSmartSourceRef.current) {
        setSmartSourceItems(prev =>
          prev.map((item, idx) => idx >= i ? { ...item, status: 'cancelled' as const } : item),
        );
        break;
      }
      setSmartSourceItems(prev =>
        prev.map((item, idx) => idx === i ? { ...item, status: 'processing' as const } : item),
      );
      try {
        const res = await api.importKnowledgeUrl(kbId, { url: items[i].url, title: items[i].name });
        setSmartSourceItems(prev =>
          prev.map((item, idx) =>
            idx === i ? { ...item, status: 'done' as const, chunkCount: res.data.chunk_count || 0 } : item,
          ),
        );
      } catch (err: any) {
        const errorMsg = err?.response?.data?.detail || 'Import failed';
        setSmartSourceItems(prev =>
          prev.map((item, idx) =>
            idx === i ? { ...item, status: 'failed' as const, error: errorMsg } : item,
          ),
        );
      }
    }

    setSmartSourceRunning(false);
    setSmartSourceDone(true);
    await Promise.all([loadDocs(), loadKb()]);
    onRefreshList?.();
  };

  const handleSmartSourceCancel = () => {
    cancelSmartSourceRef.current = true;
  };

  const handleSmartSourceClose = () => {
    if (smartSourceRunning) return;
    setSmartSourceOpen(false);
    setSmartSourceInput('');
    setSmartSourceItems([]);
    setSmartSourceRunning(false);
    setSmartSourceDone(false);
    cancelSmartSourceRef.current = false;
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    setSemanticFallback(false);
    try {
      if (searchMode === 'semantic') {
        const res = await api.semanticSearchKnowledgeBases([kbId], searchQuery.trim(), searchLimit);
        setSearchResults(normalizeSearchResults(res.data));
        setSemanticFallback(Boolean(res.data?.semantic_fallback));
      } else {
        const res = await api.searchKnowledgeBase(kbId, searchQuery.trim(), searchLimit);
        setSearchResults(normalizeSearchResults(res.data));
      }
    } catch {
      message.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleGenerateSuggestedTasks = async () => {
    setGeneratingSuggestedTasks(true);
    try {
      const res = await api.generateKnowledgeBaseSuggestedTasks(kbId, { count: 8 });
      setSuggestedTasks(res.data.suggested_tasks || []);
      setActiveTab('suggested');
      message.success(`Generated ${res.data.generated_count || 0} suggested tasks`);
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Failed to generate suggested tasks');
    } finally {
      setGeneratingSuggestedTasks(false);
    }
  };

  const handleSuggestedTaskFieldChange = (
    taskId: string,
    field: keyof SuggestedTaskTemplate,
    value: any,
  ) => {
    setSuggestedTasks(prev => prev.map(task => (
      task.id === taskId ? { ...task, [field]: value } : task
    )));
  };

  const handleSaveSuggestedTask = async (task: SuggestedTaskTemplate) => {
    if (!task.name.trim()) {
      message.warning('Suggested task name is required');
      return;
    }
    setSavingSuggestedTaskId(task.id);
    try {
      await api.updateKnowledgeBaseSuggestedTask(kbId, task.id, {
        name: task.name.trim(),
        description: task.description.trim(),
        suggested_prompt: task.suggested_prompt.trim(),
        recommended_mode: task.recommended_mode,
        suggested_knowledge_base_ids: task.suggested_knowledge_base_ids?.length
          ? task.suggested_knowledge_base_ids
          : [kbId],
        tags: task.tags || [],
      });
      message.success('Suggested task saved');
      await loadSuggestedTasks();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Failed to save suggested task');
    } finally {
      setSavingSuggestedTaskId('');
    }
  };

  const handleDeleteSuggestedTask = async (task: SuggestedTaskTemplate) => {
    Modal.confirm({
      className: 'praxys-dark-confirm-modal',
      title: 'Delete suggested task?',
      content: `This will remove "${task.name}".`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        await api.deleteKnowledgeBaseSuggestedTask(kbId, task.id);
        message.success('Suggested task deleted');
        await loadSuggestedTasks();
      },
    });
  };

  const handleLaunchSuggestedTask = async (task: SuggestedTaskTemplate) => {
    const prompt = (task.suggested_prompt || task.description || task.name || '').trim();
    if (!prompt) {
      message.warning('Add a suggested prompt before launching');
      return;
    }
    setLaunchingSuggestedTaskId(task.id);
    try {
      await api.executeTask(
        prompt,
        task.recommended_mode === 'chat' ? 'supervisor' : 'autonomous',
        '',
        '',
        '',
        {},
        task.suggested_knowledge_base_ids?.length ? task.suggested_knowledge_base_ids : [kbId],
        '',
        task.recommended_mode === 'deep_search' ? 'deep_search' : 'standard',
      );
      message.success('Task launched from suggested task');
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Failed to launch task');
    } finally {
      setLaunchingSuggestedTaskId('');
    }
  };

  const copyText = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      message.success('Chunk copied');
    } catch {
      message.error('Copy failed');
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.updateKnowledgeBase(kbId, {
        name: settingsName.trim(),
        description: settingsDescription.trim(),
        chunking_strategy: settingsChunking,
        embedding_provider: settingsProvider,
        embedding_model: settingsModel,
        status: settingsStatus,
      });
      message.success('Knowledge Base updated');
      await loadKb();
      onRefreshList?.();
    } catch {
      message.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleShareKnowledgeBase = async () => {
    const target = shareUserId.trim();
    if (!target) {
      message.warning('User email or user id is required');
      return;
    }
    setSharingKb(true);
    try {
      await api.shareKnowledgeBase(kbId, {
        shared_with_user_id: target,
        permission: 'edit',
      });
      message.success('Knowledge Base shared');
      setShareUserId('');
      await loadShares();
      onRefreshList?.();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Failed to share Knowledge Base');
    } finally {
      setSharingKb(false);
    }
  };

  const handleRevokeShare = (share: api.KnowledgeBaseShare) => {
    Modal.confirm({
      className: 'praxys-dark-confirm-modal',
      title: 'Remove collaborator?',
      content: `${share.shared_with_user_id} will lose access to this Knowledge Base.`,
      okText: 'Remove',
      okType: 'danger',
      onOk: async () => {
        await api.deleteKnowledgeBaseShare(kbId, share.shared_with_user_id);
        message.success('Collaborator removed');
        await loadShares();
        onRefreshList?.();
      },
    });
  };

  const handleArchive = async () => {
    try {
      await api.deleteKnowledgeBase(kbId);
      message.success('Knowledge Base archived');
      onRefreshList?.();
      onBack?.();
    } catch {
      message.error('Failed to archive Knowledge Base');
    }
  };

  const handleDeleteDocument = (doc: KBDoc) => {
    Modal.confirm({
      className: 'praxys-dark-confirm-modal',
      title: 'Delete document?',
      content: `This will permanently remove "${doc.title}" and all chunks indexed from it.`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        await api.deleteKnowledgeDocument(kbId, doc.id);
        message.success('Document deleted');
        if (chunkDocFilter === doc.id) setChunkDocFilter('');
        await Promise.all([loadKb(), loadDocs(), loadChunks()]);
        onRefreshList?.();
      },
    });
  };

  const handleDeleteChunk = (chunk: KBChunk) => {
    Modal.confirm({
      className: 'praxys-dark-confirm-modal',
      title: 'Delete indexed chunk?',
      content: `This will permanently remove chunk #${chunk.chunk_index}.`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        await api.deleteKnowledgeChunk(kbId, chunk.id);
        message.success('Chunk deleted');
        await Promise.all([loadKb(), loadDocs(), loadChunks()]);
        onRefreshList?.();
      },
    });
  };

  const filteredChunks = useMemo(
    () => chunks.filter(chunk => !chunkQuery.trim() || chunk.text.toLowerCase().includes(chunkQuery.toLowerCase())),
    [chunks, chunkQuery],
  );

  const docColumns: ColumnsType<KBDoc> = [
    {
      title: 'Document',
      dataIndex: 'title',
      key: 'title',
      render: (_, row) => (
        <Space>
          <FileTextOutlined style={{ color: '#38bdf8' }} />
          <span>
            <Text className="kb-table-title">{row.title}</Text>
            {row.error_message ? <Text className="kb-table-error">{row.error_message}</Text> : null}
          </span>
        </Space>
      ),
    },
    {
      title: 'Source',
      dataIndex: 'source_type',
      key: 'source_type',
      width: 110,
      render: (v: string) => <Tag color={SOURCE_COLOR[v] || 'default'}>{v || 'UNKNOWN'}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (v: string, row) => (
        <Space direction="vertical" size={2}>
          <Tag color={DOC_STATUS_COLOR[v] || 'default'} icon={v === 'PROCESSING' ? <LoadingOutlined spin /> : undefined}>
            {v}
          </Tag>
          {v === 'PROCESSING' && typeof row.processing_progress_percent === 'number' ? (
            <Progress
              percent={row.processing_progress_percent}
              size="small"
              showInfo={false}
              status="active"
              strokeColor="#38bdf8"
              style={{ width: 96, marginTop: 2 }}
            />
          ) : null}
        </Space>
      ),
    },
    {
      title: 'Content Type',
      dataIndex: 'content_type',
      key: 'content_type',
      width: 140,
      render: (v: string) => <Text className="kb-muted">{v || '—'}</Text>,
    },
    {
      title: 'Chunks',
      dataIndex: 'chunk_count',
      key: 'chunk_count',
      width: 90,
      align: 'right',
      render: (v: number) => <Text className="kb-table-count">{v || 0}</Text>,
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (v: string) => <Text className="kb-muted">{(v || '').slice(0, 19) || '—'}</Text>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, row) => (
        <Space size={6}>
          <Button
            size="small"
            icon={<FolderOpenOutlined />}
            onClick={() => {
              setChunkDocFilter(row.id);
              setActiveTab('chunks');
            }}
          >
            Chunks
          </Button>
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteDocument(row)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  if (loadingKb && !kb) return <Spin style={{ margin: 40 }} />;

  return (
    <div className="kb-detail">
      {kb && (
        <div className="kb-detail-header">
          <div>
            <Space>
              {onBack && (
                <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
                  Back
                </Button>
              )}
              <Title level={4} style={{ margin: 0, color: '#e6edf3' }}>{kb.name}</Title>
              <Tag color={kb.status === 'ACTIVE' ? 'green' : 'default'}>{kb.status}</Tag>
              {!isOwner && <Tag color="cyan">Shared with me</Tag>}
            </Space>
            <Text className="kb-description">{kb.description || 'No description provided.'}</Text>
            <div className="kb-meta-row">
              <span>workspace: {kb.workspace_id || 'n/a'}</span>
              <span>project: {kb.project_id || 'n/a'}</span>
              <span>user: {kb.user_id || 'n/a'}</span>
            </div>
          </div>
          <Space>
            <Button icon={<UploadOutlined />} onClick={() => setActiveTab('import')}>Import</Button>
            <Button icon={<SearchOutlined />} onClick={() => setActiveTab('search')}>Search</Button>
            <Button icon={<ThunderboltOutlined />} loading={generatingSuggestedTasks} onClick={handleGenerateSuggestedTasks}>
              Generate Suggested Tasks
            </Button>
            <Button icon={<PlayCircleOutlined />} onClick={() => setActiveTab('suggested')}>Suggested Tasks</Button>
            {isOwner && <Button icon={<TeamOutlined />} onClick={() => setActiveTab('sharing')}>Share</Button>}
            {isOwner && <Button icon={<SettingOutlined />} onClick={() => setActiveTab('settings')}>Settings</Button>}
          </Space>
        </div>
      )}

      <Tabs
        className="kb-tabs"
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key as typeof activeTab)}
        items={[
          {
            key: 'overview',
            label: 'Overview',
            children: (
              <div className="kb-overview-grid">
                <Card className="kb-stat-card">
                  <Text className="kb-stat-label">Documents</Text>
                  <Title level={4}>{kb?.document_count || docs.length}</Title>
                </Card>
                <Card className="kb-stat-card">
                  <Text className="kb-stat-label">Chunks</Text>
                  <Title level={4}>{kb?.chunk_count || chunks.length}</Title>
                </Card>
                <Card className="kb-stat-card">
                  <Text className="kb-stat-label">Chunking Strategy</Text>
                  <Title level={5}>{kb?.chunking_strategy || 'n/a'}</Title>
                </Card>
                <Card className="kb-stat-card">
                  <Text className="kb-stat-label">Embedding Model</Text>
                  <Title level={5}>{kb?.embedding_model || 'n/a'}</Title>
                </Card>
              </div>
            ),
          },
          {
            key: 'documents',
            label: 'Documents',
            children: (
              <div>
                <div className="kb-tab-actions">
                  <Button size="small" icon={<ReloadOutlined />} onClick={loadDocs} loading={loadingDocs}>Refresh</Button>
                </div>
                <Table
                  className="praxys-dark-table"
                  dataSource={docs}
                  columns={docColumns}
                  rowKey="id"
                  size="small"
                  pagination={{ pageSize: 10, showSizeChanger: false }}
                  loading={loadingDocs}
                  locale={{
                    emptyText: 'Import text, URLs, PDFs, spreadsheets, or documents to begin indexing.',
                  }}
                />
              </div>
            ),
          },
          {
            key: 'import',
            label: 'Import',
            children: (
              <div className="kb-import-grid">
                <Card className="kb-import-card" title={<span><FileTextOutlined /> Paste Text</span>}>
                  <Text className="kb-muted">Add notes, SOPs, or snippets directly.</Text>
                  <Input
                    placeholder="Document title"
                    value={importTitle}
                    onChange={e => setImportTitle(e.target.value)}
                  />
                  <TextArea
                    placeholder="Paste text content"
                    rows={5}
                    value={importText}
                    onChange={e => setImportText(e.target.value)}
                  />
                  <Button type="primary" loading={importing} onClick={handleImportText}>Import Text</Button>
                </Card>

                <Card className="kb-import-card" title={<span><LinkOutlined /> Import URL</span>}>
                  <Text className="kb-muted">Fetch web content and index it for retrieval.</Text>
                  <Input
                    prefix={<LinkOutlined />}
                    placeholder="https://example.com"
                    value={importUrl}
                    onChange={e => setImportUrl(e.target.value)}
                  />
                  <Input
                    placeholder="Title (optional)"
                    value={importUrlTitle}
                    onChange={e => setImportUrlTitle(e.target.value)}
                  />
                  <Button type="primary" loading={importing} onClick={handleImportUrl}>Import URL</Button>
                </Card>

                <Card className="kb-import-card" title={<span><CloudUploadOutlined /> Upload File</span>}>
                  <Text className="kb-muted">Supported: PDF, DOCX, TXT, MD, CSV, XLSX, HTML, JSON, JSONL, SPSS (.sav), and images</Text>
                  <Upload.Dragger
                    className="kb-upload-dragger"
                    multiple={false}
                    accept={KB_UPLOAD_ACCEPT}
                    beforeUpload={handleFileUpload}
                    showUploadList={false}
                  >
                    <p className="ant-upload-drag-icon"><UploadOutlined style={{ color: '#38bdf8' }} /></p>
                    <p className="ant-upload-text">Drop a file here or click to upload</p>
                    <p className="ant-upload-hint">PDF, DOCX, TXT, MD, CSV, XLSX, HTML, JSON, JSONL, SPSS (.sav), PNG, JPG, WEBP, GIF, TIFF</p>
                  </Upload.Dragger>
                  <input
                    ref={folderUploadInputRef}
                    type="file"
                    multiple
                    accept={KB_UPLOAD_ACCEPT}
                    style={{ display: 'none' }}
                    onChange={handleDirectorySelection}
                  />
                  <Button
                    icon={<FolderOpenOutlined />}
                    loading={importing}
                    style={{ marginTop: 12 }}
                    onClick={() => folderUploadInputRef.current?.click()}
                  >
                    Upload Folder
                  </Button>
                </Card>

                <Card className="kb-import-card" title={<span><BookOutlined /> Import Artifact</span>}>
                  <Text className="kb-muted">Select a previously indexed artifact or chunk library to pull into this KB.</Text>
                  <Select
                    showSearch
                    style={{ width: '100%' }}
                    placeholder="Search or select an artifact…"
                    value={artifactId || undefined}
                    loading={loadingArtifacts}
                    filterOption={false}
                    onSearch={v => setArtifactSearch(v)}
                    onChange={(val) => {
                      setArtifactId(val);
                      const found = artifacts.find(a => a.id === val);
                      // Always sync the title with the selected artifact's filename
                      if (found) setArtifactTitle(found.filename || '');
                    }}
                    notFoundContent={loadingArtifacts ? <Spin size="small" /> : (
                      artifactId
                        ? null
                        : <span style={{ color: '#6b7280', fontSize: 12 }}>No artifacts found. You can type an ID directly.</span>
                    )}
                    options={[
                      ...artifacts
                        .filter(a => {
                          if (!artifactSearch.trim()) return true;
                          const q = artifactSearch.toLowerCase();
                          return (
                            (a.filename || '').toLowerCase().includes(q) ||
                            a.id.toLowerCase().includes(q) ||
                            (a.task_id || '').toLowerCase().includes(q)
                          );
                        })
                        .map(a => ({
                          value: a.id,
                          label: (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <span style={{ fontWeight: 500, color: '#e6edf3', fontSize: 13 }}>{a.filename || a.id}</span>
                              <span style={{ color: '#6b7280', fontSize: 11 }}>
                                {a.content_type || 'artifact'}{a.task_id ? ` · task: ${a.task_id.slice(0, 8)}` : ''}{a.size_bytes ? ` · ${(a.size_bytes / 1024).toFixed(1)} KB` : ''}
                              </span>
                            </div>
                          ),
                        })),
                    ]}
                    dropdownRender={(menu) => (
                      <div>
                        {menu}
                        <div style={{ padding: '6px 12px', borderTop: '1px solid #2a2a2a' }}>
                          <Text style={{ fontSize: 11, color: '#6b7280' }}>Or type an artifact ID directly in the field above</Text>
                        </div>
                      </div>
                    )}
                    allowClear
                    onClear={() => { setArtifactId(''); setArtifactTitle(''); setArtifactSearch(''); }}
                  />
                  <Input
                    placeholder="Title (optional)"
                    value={artifactTitle}
                    onChange={e => setArtifactTitle(e.target.value)}
                    style={{ marginTop: 8 }}
                  />
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <Button
                      size="small"
                      icon={<ReloadOutlined />}
                      loading={loadingArtifacts}
                      onClick={loadArtifacts}
                    >
                      Refresh List
                    </Button>
                    <Button type="primary" loading={importing} onClick={handleImportArtifact} disabled={!artifactId.trim()}>
                      Import Artifact
                    </Button>
                  </div>
                </Card>

                <Card className="kb-import-card" title={<span><ThunderboltOutlined /> Smart Source</span>}>
                  <Text className="kb-muted">
                    Paste a list of sources (one per line) in the format:
                    <br />
                    <code style={{ color: '#38bdf8', fontSize: 11 }}>Source Name{'<tab>'}URL</code>
                  </Text>
                  <Button
                    type="primary"
                    icon={<ThunderboltOutlined />}
                    style={{ marginTop: 8 }}
                    onClick={() => setSmartSourceOpen(true)}
                  >
                    Open Smart Source
                  </Button>
                </Card>
              </div>
            ),
          },
          {
            key: 'search',
            label: 'Search',
            children: (
              <div>
                <div className="kb-search-bar">
                  <Input
                    prefix={<SearchOutlined />}
                    placeholder="Ask this knowledge base a question"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    onPressEnter={handleSearch}
                  />
                  <Radio.Group
                    value={searchMode}
                    onChange={e => setSearchMode(e.target.value)}
                    optionType="button"
                    buttonStyle="solid"
                    options={[
                      { label: 'Keyword', value: 'keyword' },
                      { label: 'Semantic', value: 'semantic' },
                    ]}
                  />
                  <Select
                    value={searchLimit}
                    onChange={setSearchLimit}
                    style={{ width: 90 }}
                    options={[5, 8, 10, 20].map(v => ({ value: v, label: `Top ${v}` }))}
                  />
                  <Button type="primary" icon={<SearchOutlined />} loading={searching} onClick={handleSearch}>
                    Search
                  </Button>
                </div>

                {semanticFallback && (
                  <Alert
                    type="info"
                    showIcon
                    message="Semantic search unavailable — showing keyword-style results"
                    style={{ marginBottom: 12 }}
                  />
                )}

                <div className="kb-search-results">
                  {searchResults.map((result, idx) => (
                    <Card key={`${result.chunk_id}-${idx}`} className="kb-result-card">
                      <div className="kb-result-top">
                        <Space>
                          <Tag color="cyan">{result.document_title}</Tag>
                          <Tag color="blue">score: {result.score.toFixed(3)}</Tag>
                        </Space>
                        <Button size="small" icon={<CopyOutlined />} onClick={() => copyText(result.text)}>
                          Copy chunk
                        </Button>
                      </div>
                      <Paragraph className="kb-result-text">{result.text}</Paragraph>
                      <Collapse ghost>
                        <Collapse.Panel header="Metadata" key="meta">
                          <pre className="kb-metadata-json">{JSON.stringify(result.metadata || {}, null, 2)}</pre>
                        </Collapse.Panel>
                      </Collapse>
                    </Card>
                  ))}

                  {searchResults.length === 0 && !searching ? (
                    <div className="kb-empty-block">
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={<span>No matching chunks found. Try a broader query.</span>}
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            ),
          },
          {
            key: 'suggested',
            label: 'Suggested Tasks',
            children: (
              <div>
                <div className="kb-tab-actions" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                  <Space>
                    <Button
                      type="primary"
                      icon={<ThunderboltOutlined />}
                      loading={generatingSuggestedTasks}
                      onClick={handleGenerateSuggestedTasks}
                    >
                      Generate Suggested Tasks
                    </Button>
                    <Button size="small" icon={<ReloadOutlined />} loading={loadingSuggestedTasks} onClick={loadSuggestedTasks}>
                      Refresh
                    </Button>
                  </Space>
                  <Text className="kb-muted">
                    Review and edit before launching. Tasks are never auto-run.
                  </Text>
                </div>

                {suggestedTasks.length === 0 ? (
                  <div className="kb-empty-block">
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={<span>No suggested tasks yet. Generate tasks from this Knowledge Base.</span>}
                    >
                      <Button type="primary" icon={<ThunderboltOutlined />} loading={generatingSuggestedTasks} onClick={handleGenerateSuggestedTasks}>
                        Generate Suggested Tasks
                      </Button>
                    </Empty>
                  </div>
                ) : (
                  <div className="kb-search-results">
                    {suggestedTasks.map(task => (
                      <Card key={task.id} className="kb-result-card">
                        <Space direction="vertical" style={{ width: '100%' }} size={10}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 220px', gap: 10 }}>
                            <div>
                              <Text className="kb-field-label">Name</Text>
                              <Input
                                value={task.name}
                                onChange={e => handleSuggestedTaskFieldChange(task.id, 'name', e.target.value)}
                                placeholder="Suggested task name"
                              />
                            </div>
                            <div>
                              <Text className="kb-field-label">Recommended Mode</Text>
                              <Select
                                value={task.recommended_mode}
                                onChange={v => handleSuggestedTaskFieldChange(task.id, 'recommended_mode', v)}
                                options={[
                                  { value: 'chat', label: 'Chat' },
                                  { value: 'deep_search', label: 'Deep Search' },
                                  { value: 'autonomous', label: 'Agent/Autonomous' },
                                ]}
                              />
                            </div>
                          </div>
                          <div>
                            <Text className="kb-field-label">Description</Text>
                            <TextArea
                              rows={2}
                              value={task.description}
                              onChange={e => handleSuggestedTaskFieldChange(task.id, 'description', e.target.value)}
                              placeholder="What this suggested task is for"
                            />
                          </div>
                          <div>
                            <Text className="kb-field-label">Suggested Prompt</Text>
                            <TextArea
                              rows={3}
                              value={task.suggested_prompt}
                              onChange={e => handleSuggestedTaskFieldChange(task.id, 'suggested_prompt', e.target.value)}
                              placeholder="Prompt users can launch quickly"
                            />
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                            <div>
                              <Text className="kb-field-label">Suggested Knowledge Bases</Text>
                              <Select
                                mode="multiple"
                                value={task.suggested_knowledge_base_ids || [kbId]}
                                onChange={v => handleSuggestedTaskFieldChange(task.id, 'suggested_knowledge_base_ids', v)}
                                options={[{ value: kbId, label: kb?.name || kbId }]}
                              />
                            </div>
                            <div>
                              <Text className="kb-field-label">Tags</Text>
                              <Select
                                mode="tags"
                                value={task.tags || []}
                                onChange={v => handleSuggestedTaskFieldChange(task.id, 'tags', v)}
                                placeholder="e.g. risk, summary, planning"
                                tokenSeparators={[',']}
                              />
                            </div>
                          </div>
                          <Space>
                            <Button
                              type="primary"
                              loading={savingSuggestedTaskId === task.id}
                              onClick={() => handleSaveSuggestedTask(task)}
                            >
                              Save
                            </Button>
                            <Button
                              icon={<PlayCircleOutlined />}
                              loading={launchingSuggestedTaskId === task.id}
                              onClick={() => handleLaunchSuggestedTask(task)}
                            >
                              Launch Task
                            </Button>
                            <Button
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => handleDeleteSuggestedTask(task)}
                            >
                              Delete
                            </Button>
                          </Space>
                        </Space>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            ),
          },
          {
            key: 'chunks',
            label: 'Chunks',
            children: (
              <div>
                <div className="kb-chunk-toolbar">
                  <Select
                    placeholder="Filter by document"
                    allowClear
                    value={chunkDocFilter || undefined}
                    onChange={value => setChunkDocFilter(value || '')}
                    options={docs.map(d => ({ value: d.id, label: d.title }))}
                    style={{ minWidth: 260 }}
                  />
                  <Input
                    prefix={<SearchOutlined />}
                    placeholder="Search chunks"
                    value={chunkQuery}
                    onChange={e => setChunkQuery(e.target.value)}
                    style={{ maxWidth: 320 }}
                  />
                  <Button size="small" icon={<ReloadOutlined />} onClick={loadChunks} loading={loadingChunks}>Refresh</Button>
                </div>

                <div className="kb-chunk-list">
                  {filteredChunks.map(chunk => (
                    <Card key={chunk.id} className="kb-chunk-card">
                      <div className="kb-chunk-meta">
                        <Space size={6} wrap>
                          <Tag color="blue">Chunk #{chunk.chunk_index}</Tag>
                          <Tag color="default">{chunk.token_count} tokens</Tag>
                          <Tag color="purple">{docTitleById[chunk.document_id] || (chunk.document_id || '').slice(0, 8) || 'Unknown'}</Tag>
                        </Space>
                        <Button
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleDeleteChunk(chunk)}
                        >
                          Delete
                        </Button>
                      </div>
                      <Paragraph className="kb-result-text">{chunk.text}</Paragraph>
                      <Collapse ghost>
                        <Collapse.Panel header="Metadata" key="meta">
                          <pre className="kb-metadata-json">{JSON.stringify(chunk.metadata || {}, null, 2)}</pre>
                        </Collapse.Panel>
                      </Collapse>
                    </Card>
                  ))}

                  {!loadingChunks && filteredChunks.length === 0 ? (
                    <div className="kb-empty-block">
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={<span>No chunks found.</span>}
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            ),
          },
          isOwner ? {
            key: 'sharing',
            label: 'Sharing',
            children: (
              <Card className="kb-settings-card">
                <Space direction="vertical" style={{ width: '100%' }} size={16}>
                  <div>
                    <Text className="kb-field-label">Add collaborator</Text>
                    <Space.Compact style={{ width: '100%' }}>
                      <Input
                        prefix={<UserAddOutlined style={{ color: '#8b949e' }} />}
                        placeholder="User email or user id"
                        value={shareUserId}
                        onChange={e => setShareUserId(e.target.value)}
                        onPressEnter={handleShareKnowledgeBase}
                      />
                      <Button
                        type="primary"
                        icon={<TeamOutlined />}
                        loading={sharingKb}
                        onClick={handleShareKnowledgeBase}
                      >
                        Share
                      </Button>
                    </Space.Compact>
                  </div>
                  <div>
                    <Text className="kb-field-label">Collaborators</Text>
                    <Table
                      className="praxys-dark-table"
                      dataSource={shares}
                      rowKey={(row) => row.id || row.shared_with_user_id}
                      size="small"
                      loading={loadingShares}
                      pagination={false}
                      locale={{ emptyText: 'No collaborators yet.' }}
                      columns={[
                        {
                          title: 'User',
                          dataIndex: 'shared_with_user_id',
                          render: (value: string) => <Text style={{ color: '#e6edf3' }}>{value}</Text>,
                        },
                        {
                          title: 'Permission',
                          dataIndex: 'permission',
                          width: 140,
                          render: (value: string) => <Tag color="cyan">{value || 'edit'}</Tag>,
                        },
                        {
                          title: 'Shared',
                          dataIndex: 'created_at',
                          width: 180,
                          render: (value: string) => <Text className="kb-muted">{(value || '').slice(0, 19) || 'n/a'}</Text>,
                        },
                        {
                          title: '',
                          width: 120,
                          render: (_: any, row: api.KnowledgeBaseShare) => (
                            <Button danger size="small" icon={<DeleteOutlined />} onClick={() => handleRevokeShare(row)}>
                              Remove
                            </Button>
                          ),
                        },
                      ]}
                    />
                  </div>
                </Space>
              </Card>
            ),
          } : null,
          isOwner ? {
            key: 'settings',
            label: 'Settings',
            children: (
              <Card className="kb-settings-card">
                <Space direction="vertical" style={{ width: '100%' }} size={14}>
                  <div>
                    <Text className="kb-field-label">KB Name</Text>
                    <Input value={settingsName} onChange={e => setSettingsName(e.target.value)} />
                  </div>
                  <div>
                    <Text className="kb-field-label">Description</Text>
                    <TextArea rows={3} value={settingsDescription} onChange={e => setSettingsDescription(e.target.value)} />
                  </div>
                  <div className="kb-settings-grid">
                    <div>
                      <Text className="kb-field-label">Chunking strategy</Text>
                      <Select value={settingsChunking} onChange={setSettingsChunking} options={[
                        { value: 'recursive', label: 'Recursive' },
                        { value: 'fixed', label: 'Fixed window' },
                        { value: 'semantic', label: 'Semantic' },
                      ]} />
                    </div>
                    <div>
                      <Text className="kb-field-label">Embedding provider</Text>
                      <Select value={settingsProvider} onChange={setSettingsProvider} options={[
                        { value: 'openai', label: 'OpenAI' },
                        { value: 'azure_openai', label: 'Azure OpenAI' },
                        { value: 'local', label: 'Local' },
                      ]} />
                    </div>
                    <div>
                      <Text className="kb-field-label">Embedding model</Text>
                      <Input value={settingsModel} onChange={e => setSettingsModel(e.target.value)} />
                    </div>
                    <div>
                      <Text className="kb-field-label">Status</Text>
                      <Select value={settingsStatus} onChange={setSettingsStatus} options={[
                        { value: 'ACTIVE', label: 'Active' },
                        { value: 'ARCHIVED', label: 'Archived' },
                      ]} />
                    </div>
                  </div>
                  <Space>
                    <Button type="primary" loading={savingSettings} onClick={handleSaveSettings}>Save</Button>
                    <Button danger onClick={handleArchive}>Archive</Button>
                  </Space>
                </Space>
              </Card>
            ),
          } : null,
        ].filter(Boolean) as any}
      />

      <Modal
        title={<span style={{ color: '#e6edf3' }}><ThunderboltOutlined style={{ color: '#38bdf8', marginRight: 8 }} />Smart Source Import</span>}
        open={smartSourceOpen}
        onCancel={smartSourceRunning ? undefined : handleSmartSourceClose}
        closable={!smartSourceRunning}
        maskClosable={!smartSourceRunning}
        width={640}
        footer={
          smartSourceDone ? (
            <Button type="primary" onClick={handleSmartSourceClose}>Close</Button>
          ) : smartSourceRunning ? (
            <Button danger onClick={handleSmartSourceCancel}>Cancel</Button>
          ) : (
            <Space>
              <Button onClick={handleSmartSourceClose}>Cancel</Button>
              <Button type="primary" icon={<ThunderboltOutlined />} onClick={handleSmartSourceStart}>
                Start Import
              </Button>
            </Space>
          )
        }
      >
        {smartSourceItems.length === 0 ? (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text className="kb-muted">
              Paste a list of sources below, one per line. Each line should contain a source name
              and URL separated by a tab character:
            </Text>
            <code style={{ display: 'block', color: '#38bdf8', fontSize: 11, marginBottom: 4 }}>
              Lincoln University (PA) History{'  <tab>  '}https://www.lincoln.edu/about/history.html
            </code>
            <TextArea
              rows={10}
              placeholder={`Lincoln University (PA) History\thttps://www.lincoln.edu/about/history.html\nLincoln University (PA) Admissions\thttps://www.lincoln.edu/admissions/index.html`}
              value={smartSourceInput}
              onChange={e => setSmartSourceInput(e.target.value)}
              style={{ fontFamily: 'monospace', fontSize: 12 }}
            />
          </Space>
        ) : (
          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            {smartSourceItems.map((item, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  padding: '8px 4px',
                  borderBottom: '1px solid #21262d',
                }}
              >
                <div style={{ marginTop: 2, flexShrink: 0 }}>
                  {item.status === 'done' && <CheckCircleOutlined style={{ color: '#3fb950', fontSize: 16 }} />}
                  {item.status === 'processing' && <LoadingOutlined style={{ color: '#38bdf8', fontSize: 16 }} spin />}
                  {item.status === 'failed' && <CloseCircleOutlined style={{ color: '#f85149', fontSize: 16 }} />}
                  {item.status === 'cancelled' && <MinusCircleOutlined style={{ color: '#6b7280', fontSize: 16 }} />}
                  {item.status === 'pending' && <MinusCircleOutlined style={{ color: '#6b7280', fontSize: 16 }} />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <Text strong style={{ color: '#e6edf3', fontSize: 13 }}>{item.name}</Text>
                    {item.status === 'done' && (
                      <Tag color="green">{item.chunkCount} chunks</Tag>
                    )}
                    {item.status === 'processing' && <Tag color="blue">Processing…</Tag>}
                    {item.status === 'failed' && <Tag color="red">Failed</Tag>}
                    {item.status === 'cancelled' && <Tag color="default">Cancelled</Tag>}
                    {item.status === 'pending' && <Tag color="default">Pending</Tag>}
                  </div>
                  <Text style={{ color: '#8b949e', fontSize: 11, wordBreak: 'break-all' }}>{item.url}</Text>
                  {item.error && (
                    <Text style={{ color: '#f85149', fontSize: 11, display: 'block' }}>{item.error}</Text>
                  )}
                </div>
              </div>
            ))}
            {smartSourceDone && (
              <div style={{ padding: '12px 4px 4px', textAlign: 'center' }}>
                <Text style={{ color: '#3fb950' }}>
                  <CheckCircleOutlined style={{ marginRight: 6 }} />
                  Import complete — {smartSourceItems.filter(i => i.status === 'done').length} of {smartSourceItems.length} sources indexed successfully.
                </Text>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default KnowledgeBaseDetail;
